import React, { useState, useEffect } from "react";

const FootballLoading = () => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setLoadingComplete(true);
          setTimeout(() => setShowContent(true), 1200);
          return 100;
        }

        const remaining = 100 - prev;
        let increment;

        if (remaining > 30) {
          increment = Math.random() * 8 + 4;
        } else if (remaining > 10) {
          increment = Math.random() * 4 + 2;
        } else {
          increment = Math.random() * 2 + 1;
        }

        return Math.min(prev + increment, 100);
      });
    }, 150);

    return () => clearInterval(interval);
  }, []);

  if (!showContent) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-black relative overflow-hidden">
        {/* Animated Background Particles */}
        <div className="absolute inset-0">
          <div className="particle particle-1"></div>
          <div className="particle particle-2"></div>
          <div className="particle particle-3"></div>
          <div className="particle particle-4"></div>
          <div className="particle particle-5"></div>
          <div className="particle particle-6"></div>
        </div>

        {/* Animated Grid Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="grid-lines"></div>
        </div>

        {/* Pulsing Rings */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="pulse-ring pulse-ring-1"></div>
          <div className="pulse-ring pulse-ring-2"></div>
          <div className="pulse-ring pulse-ring-3"></div>
        </div>

        {/* Main loader container */}
        <div className="flex flex-col items-center space-y-12 z-10">
          {/* Large Fixed Logo with Effects */}
          <div className="relative">
            {/* Glow effect behind logo */}
            <div className="absolute -inset-8 bg-gradient-to-r from-blue-500/20 via-cyan-400/20 to-blue-500/20 rounded-full blur-2xl animate-glow-pulse"></div>

            {/* Main logo container */}
            <div className="relative w-32 h-32 rounded-full shadow-2xl overflow-hidden">
              <img
                src="https://firebasestorage.googleapis.com/v0/b/towin-fe4cc.appspot.com/o/logoBlgo%2Fnuevo%20logo.png?alt=media&token=5bcd17e0-99cd-4e7c-a45b-d2928a449a40"
                alt="Logo"
                className="w-full h-full object-cover"
              />

              {/* Shimmer effect overlay */}
              <div className="absolute inset-0 shimmer-overlay"></div>

              {/* Light reflection */}
              <div className="absolute top-4 left-4 w-8 h-8 bg-white rounded-full opacity-30 blur-sm animate-shine"></div>

              {/* Border glow */}
              <div className="absolute inset-0 rounded-full border-2 border-cyan-400/50 animate-border-glow"></div>
            </div>

            {/* Floating sparkles around logo */}
            <div className="sparkle sparkle-1"></div>
            <div className="sparkle sparkle-2"></div>
            <div className="sparkle sparkle-3"></div>
            <div className="sparkle sparkle-4"></div>
          </div>

          {/* Enhanced Brand text */}
          <div className="text-center relative">
            <h1 className="text-4xl font-light text-white tracking-wider mb-3 animate-text-glow">
              FOOTBALL
            </h1>
            <div className="text-lg text-gray-300 font-light tracking-widest animate-fade-in-delay">
              ANALYTICS
            </div>
            {/* Text underline effect */}
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent mx-auto mt-4 animate-line-expand"></div>
          </div>

          {/* Enhanced progress bar */}
          <div className="relative">
            <div className="w-80 h-2 bg-gray-800 rounded-full relative overflow-hidden shadow-inner">
              {/* Background pattern */}
              <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-gray-700"></div>

              {/* Progress fill */}
              <div
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-500 rounded-full transition-all duration-300 ease-out shadow-lg progress-glow"
                style={{ width: `${loadingProgress}%` }}
              ></div>

              {/* Animated light sweep */}
              <div className="absolute top-0 left-0 h-full w-4 bg-white opacity-30 rounded-full animate-sweep transform -translate-x-full"></div>
            </div>

            {/* Progress dots indicator */}
            <div className="flex justify-between mt-3 px-1">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    loadingProgress > i * 25
                      ? "bg-cyan-400 shadow-lg shadow-cyan-400/50"
                      : "bg-gray-600"
                  }`}
                ></div>
              ))}
            </div>
          </div>

          {/* Enhanced progress percentage */}
          <div className="text-center">
            <div className="text-white font-mono text-2xl mb-2 animate-pulse-number">
              {loadingProgress >= 100 ? "100" : Math.floor(loadingProgress)}%
            </div>
            <div className="text-gray-400 text-sm animate-pulse">
              Loading your dashboard...
            </div>
          </div>
        </div>

        {/* Success animation overlay */}
        {loadingComplete && (
          <>
            <div className="absolute inset-0 success-flash"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="success-checkmark">✓</div>
            </div>
          </>
        )}

        <style jsx>{`
          /* Particle animations */
          .particle {
            position: absolute;
            background: radial-gradient(
              circle,
              rgba(59, 130, 246, 0.8) 0%,
              transparent 70%
            );
            border-radius: 50%;
            animation: float-particle 6s ease-in-out infinite;
          }

          .particle-1 {
            width: 4px;
            height: 4px;
            top: 20%;
            left: 15%;
            animation-delay: 0s;
          }
          .particle-2 {
            width: 6px;
            height: 6px;
            top: 60%;
            left: 80%;
            animation-delay: 1s;
          }
          .particle-3 {
            width: 3px;
            height: 3px;
            top: 30%;
            left: 70%;
            animation-delay: 2s;
          }
          .particle-4 {
            width: 5px;
            height: 5px;
            top: 80%;
            left: 20%;
            animation-delay: 3s;
          }
          .particle-5 {
            width: 4px;
            height: 4px;
            top: 15%;
            left: 60%;
            animation-delay: 4s;
          }
          .particle-6 {
            width: 7px;
            height: 7px;
            top: 70%;
            left: 40%;
            animation-delay: 5s;
          }

          @keyframes float-particle {
            0%,
            100% {
              transform: translateY(0px) scale(1);
              opacity: 0.7;
            }
            50% {
              transform: translateY(-20px) scale(1.2);
              opacity: 1;
            }
          }

          /* Grid background */
          .grid-lines {
            background-image: linear-gradient(
                rgba(59, 130, 246, 0.1) 1px,
                transparent 1px
              ),
              linear-gradient(
                90deg,
                rgba(59, 130, 246, 0.1) 1px,
                transparent 1px
              );
            background-size: 50px 50px;
            width: 100%;
            height: 100%;
            animation: grid-move 20s linear infinite;
          }

          @keyframes grid-move {
            0% {
              transform: translate(0, 0);
            }
            100% {
              transform: translate(50px, 50px);
            }
          }

          /* Pulsing rings */
          .pulse-ring {
            position: absolute;
            border: 1px solid rgba(59, 130, 246, 0.3);
            border-radius: 50%;
            animation: pulse-expand 3s ease-out infinite;
          }

          .pulse-ring-1 {
            width: 200px;
            height: 200px;
            animation-delay: 0s;
          }
          .pulse-ring-2 {
            width: 300px;
            height: 300px;
            animation-delay: 1s;
          }
          .pulse-ring-3 {
            width: 400px;
            height: 400px;
            animation-delay: 2s;
          }

          @keyframes pulse-expand {
            0% {
              transform: scale(0.8);
              opacity: 0;
            }
            50% {
              opacity: 0.5;
            }
            100% {
              transform: scale(1.2);
              opacity: 0;
            }
          }

          /* Logo effects */
          @keyframes glow-pulse {
            0%,
            100% {
              opacity: 0.5;
              transform: scale(1);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.05);
            }
          }

          .animate-glow-pulse {
            animation: glow-pulse 2s ease-in-out infinite;
          }

          /* Shimmer effect */
          .shimmer-overlay {
            background: linear-gradient(
              45deg,
              transparent 30%,
              rgba(255, 255, 255, 0.3) 50%,
              transparent 70%
            );
            animation: shimmer 3s ease-in-out infinite;
          }

          @keyframes shimmer {
            0% {
              transform: translateX(-100%) translateY(-100%);
            }
            100% {
              transform: translateX(100%) translateY(100%);
            }
          }

          /* Shine effect */
          @keyframes shine {
            0%,
            100% {
              opacity: 0.3;
              transform: scale(1);
            }
            50% {
              opacity: 0.7;
              transform: scale(1.2);
            }
          }

          .animate-shine {
            animation: shine 2s ease-in-out infinite;
          }

          /* Border glow */
          @keyframes border-glow {
            0%,
            100% {
              border-color: rgba(34, 211, 238, 0.5);
              box-shadow: 0 0 20px rgba(34, 211, 238, 0.3);
            }
            50% {
              border-color: rgba(59, 130, 246, 0.8);
              box-shadow: 0 0 30px rgba(59, 130, 246, 0.5);
            }
          }

          .animate-border-glow {
            animation: border-glow 2s ease-in-out infinite;
          }

          /* Floating sparkles */
          .sparkle {
            position: absolute;
            width: 6px;
            height: 6px;
            background: radial-gradient(
              circle,
              #ffffff 0%,
              rgba(34, 211, 238, 0.8) 70%,
              transparent 100%
            );
            border-radius: 50%;
            animation: sparkle-float 4s ease-in-out infinite;
          }

          .sparkle-1 {
            top: -10px;
            left: -10px;
            animation-delay: 0s;
          }
          .sparkle-2 {
            top: -15px;
            right: -5px;
            animation-delay: 1s;
          }
          .sparkle-3 {
            bottom: -5px;
            left: -15px;
            animation-delay: 2s;
          }
          .sparkle-4 {
            bottom: -10px;
            right: -10px;
            animation-delay: 3s;
          }

          @keyframes sparkle-float {
            0%,
            100% {
              transform: translateY(0px) scale(0.8);
              opacity: 0.6;
            }
            25% {
              transform: translateY(-8px) scale(1.2);
              opacity: 1;
            }
            75% {
              transform: translateY(8px) scale(0.9);
              opacity: 0.8;
            }
          }

          /* Text effects */
          @keyframes text-glow {
            0%,
            100% {
              text-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
            }
            50% {
              text-shadow: 0 0 30px rgba(34, 211, 238, 0.8),
                0 0 40px rgba(59, 130, 246, 0.6);
            }
          }

          .animate-text-glow {
            animation: text-glow 3s ease-in-out infinite;
          }

          @keyframes line-expand {
            0% {
              width: 0;
              opacity: 0;
            }
            100% {
              width: 6rem;
              opacity: 1;
            }
          }

          .animate-line-expand {
            animation: line-expand 2s ease-out 1s both;
          }

          @keyframes fade-in-delay {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate-fade-in-delay {
            animation: fade-in-delay 1s ease-out 1.5s both;
          }

          /* Progress bar effects */
          .progress-glow {
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.6),
              inset 0 1px 0 rgba(255, 255, 255, 0.3);
          }

          @keyframes sweep {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(400%);
            }
          }

          .animate-sweep {
            animation: sweep 2s ease-in-out infinite;
          }

          /* Number pulse */
          @keyframes pulse-number {
            0%,
            100% {
              color: #ffffff;
            }
            50% {
              color: #22d3ee;
            }
          }

          .animate-pulse-number {
            animation: pulse-number 1s ease-in-out infinite;
          }

          /* Success animations */
          .success-flash {
            background: radial-gradient(
              circle,
              rgba(34, 197, 94, 0.4) 0%,
              transparent 70%
            );
            animation: success-flash-anim 1.2s ease-out;
          }

          @keyframes success-flash-anim {
            0% {
              opacity: 0;
              transform: scale(0);
            }
            50% {
              opacity: 1;
              transform: scale(1);
            }
            100% {
              opacity: 0;
              transform: scale(1.2);
            }
          }

          .success-checkmark {
            font-size: 4rem;
            color: #22c55e;
            animation: checkmark-appear 0.6s ease-out 0.6s both;
            text-shadow: 0 0 20px rgba(34, 197, 94, 0.8);
          }

          @keyframes checkmark-appear {
            0% {
              opacity: 0;
              transform: scale(0) rotate(-45deg);
            }
            100% {
              opacity: 1;
              transform: scale(1) rotate(0deg);
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-white animate-fade-in-app">
      <header className="border-b border-gray-700 bg-black bg-opacity-30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full overflow-hidden">
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/towin-fe4cc.appspot.com/o/logoBlgo%2Fnuevo%20logo.png?alt=media&token=5bcd17e0-99cd-4e7c-a45b-d2928a449a40"
                  alt="Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <h1 className="text-xl font-light tracking-wide">
                Football Analytics
              </h1>
            </div>
            <div className="text-sm text-gray-400">Premium Dashboard</div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">
                Matches Today
              </h3>
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            </div>
            <div className="text-2xl font-light">12</div>
          </div>

          <div className="bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Win Rate</h3>
              <div className="text-xs text-green-400">+5.2%</div>
            </div>
            <div className="text-2xl font-light">78.5%</div>
          </div>

          <div className="bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Revenue</h3>
              <div className="text-xs text-blue-400">Live</div>
            </div>
            <div className="text-2xl font-light">€2,450</div>
          </div>
        </div>

        <div className="bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-xl p-8 border border-gray-700">
          <div className="text-center mb-6">
            <h2 className="text-xl font-light mb-2">Next Match</h2>
            <div className="text-gray-400 text-sm">
              Champions League • 20:45
            </div>
          </div>

          <div className="flex items-center justify-center space-x-8">
            <div className="text-center">
              <div className="text-lg font-medium mb-1">PSG</div>
              <div className="text-sm text-gray-400">Paris</div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-2xl font-light text-gray-400">2</div>
              <div className="text-sm text-gray-500">:</div>
              <div className="text-2xl font-light text-gray-400">1</div>
            </div>

            <div className="text-center">
              <div className="text-lg font-medium mb-1">Liverpool</div>
              <div className="text-sm text-gray-400">England</div>
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <div className="px-4 py-2 bg-blue-600 bg-opacity-20 text-blue-400 rounded-lg text-sm">
              Analysis Available
            </div>
          </div>
        </div>
      </main>

      <style jsx>{`
        @keyframes fade-in-app {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-app {
          animation: fade-in-app 0.8s ease-out;
        }
      `}</style>
    </div>
  );
};

export default FootballLoading;
